import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusIcon } from './StatusIcon';
import { ExternalLinkIcon } from 'lucide-react';
import clsx from 'clsx';
import { canNavigateToSubtramite, getSubtramiteRoutePath } from '@/common/utils/subtramiteNavigation';
import { useNavigate } from 'react-router-dom';

interface SubtramiteListProps {
  subtramites: Array<{
    label: string;
    status: "pendiente" | "en-proceso" | "finalizado";
  }>;
  variant?: 'default' | 'compact';
  tramite: any;
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'pendiente':
      return 'Sin iniciar';
    case 'en-proceso':
      return 'En Proceso';
    case 'finalizado':
      return 'Finalizado';
    default:
      return 'Desconocido';
  }
}

const getStatusBadgeClasses = (status: string) => {
  switch (status) {
    case 'pendiente':
      return "bg-gray-100 text-gray-800";
    case 'en-proceso':
      return "bg-orange-100 text-orange-800";
    case 'finalizado':
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export const SubtramiteList: React.FC<SubtramiteListProps> = ({
  tramite,
  subtramites,
  variant = 'default',
}) => {
  const navigate = useNavigate();

  const gridClasses = clsx(
    'grid gap-2',
    {
      'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3': variant === 'default',
      'grid-cols-1 sm:grid-cols-1 lg:grid-cols-1': variant === 'compact',
    }
  );

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">Detalle por Subtrámite</h4>
      <div className={gridClasses}>
        {subtramites.map(({ label, status }) => {
          const routePath = getSubtramiteRoutePath(label, tramite.id);
          const canNavigate = canNavigateToSubtramite(status);

          return (
            <div key={label} className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <StatusIcon status={status} />
                <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <Badge className={`text-xs font-medium ${getStatusBadgeClasses(status)}`}>
                  {getStatusLabel(status)}
                </Badge>
                {canNavigate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className='text-xs text-primary'
                    disabled={!routePath || routePath === "/otros"}
                    onClick={() => routePath && navigate(routePath, { state: { tramite: tramite } })}
                  >
                    <ExternalLinkIcon className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
